// src/features/task/services/taskOperations.ts
// 跨 task 業務邏輯：dropTasksByObjectiveId, splitTask, splitTaskWithAI, optimizeTaskContent
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  serverTimestamp,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../../../config/firebase';
import {
  Task,
  TaskStatus,
  TaskCloseReason,
  SplitTaskResult,
  SplitTaskLocalResult,
  OptimizeTaskResult,
} from '../../../types/task';
import { eventBus, EVENTS } from '../../../utils/eventBus';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { logger } from '../../../utils/logger';
import { formatDateToString } from '../../../utils/dateUtils';
import { TASKS_COLLECTION, convertFirestoreTaskToTask } from './taskConverters';
// 直接 import taskCRUD 方法，避免透過 index 造成循環依賴
import { createTask, updateTask } from './taskCRUD';

const dropTasksByObjectiveId = async (objectiveId: string, userId: string): Promise<void> => {
  try {
    const q = query(
      collection(db, TASKS_COLLECTION),
      where('userId', '==', userId),
      where('objectiveId', '==', objectiveId),
      where('status', '==', 'OPEN')
    );
    const snapshot = await getDocs(q);
    if (snapshot.empty) return;

    const batch = writeBatch(db);
    const now = serverTimestamp();
    snapshot.docs.forEach(d => {
      batch.update(d.ref, {
        status: 'CLOSED' as TaskStatus,
        closeReason: 'dropped' as TaskCloseReason,
        closedAt: now,
        updatedAt: now,
      });
    });
    await batch.commit();
  } catch (error) {
    logger.error('dropTasksByObjectiveId 失敗:', error);
    throw error;
  }
};

const splitTask = async (
  parentTaskId: string,
  subtasks: Partial<Task>[],
  userId: string
): Promise<SplitTaskLocalResult> => {
  try {
    const parentTaskDoc = await getDoc(doc(db, TASKS_COLLECTION, parentTaskId));

    if (!parentTaskDoc.exists()) {
      throw new Error('父任務不存在');
    }

    const parentTask = convertFirestoreTaskToTask(parentTaskDoc);

    if (parentTask.userId !== userId) {
      throw new Error('無權限分割此任務');
    }

    const subtaskIds: string[] = [];
    for (const subtask of subtasks) {
      const newTaskData = {
        ...subtask,
        userId,
        relatedTaskID: parentTaskId,
        relatedTaskTitle: parentTask.title,
      };

      const createdTask = await createTask(newTaskData as typeof newTaskData & { title: string });
      subtaskIds.push(createdTask.id);
    }

    const updatedParentTask = await updateTask(
      parentTaskId,
      {
        status: 'CLOSED' as TaskStatus,
        closeReason: 'split' as TaskCloseReason,
        closedAt: new Date(),
        notes: parentTask.notes
          ? `${parentTask.notes}\n\n[Split into ${subtasks.length} subtasks]`
          : `[Split into ${subtasks.length} subtasks]`,
      },
      userId
    );

    eventBus.publish(EVENTS.TASK.SPLIT, parentTaskId, subtaskIds, userId);

    return {
      parentTask: updatedParentTask,
      subtaskIds,
    };
  } catch (error) {
    logger.error('分割任務失敗:', error);
    throw new Error('分割任務失敗');
  }
};

const splitTaskWithAI = async (
  task: Partial<Task>,
  userId: string,
  options?: {
    context?: Record<string, unknown>;
    currentDate?: string;
  }
): Promise<SplitTaskResult> => {
  try {
    const functions = getFunctions();
    const splitTaskFn = httpsCallable<unknown, SplitTaskResult>(functions, 'splitTask');

    const response = await splitTaskFn({
      task,
      userId,
      options: {
        currentDate: options?.currentDate || formatDateToString(new Date()),
        context: options?.context || {},
      },
    });

    return response.data;
  } catch (error) {
    logger.error('AI 任務分割失敗:', error);
    throw new Error('AI task split failed');
  }
};

const optimizeTaskContent = async (
  task: Pick<Task, 'title' | 'notes' | 'category' | 'objectiveId'>,
  userId: string
): Promise<OptimizeTaskResult> => {
  try {
    const functions = getFunctions();
    const optimizeFn = httpsCallable<unknown, OptimizeTaskResult>(functions, 'optimizeTaskContent');
    const response = await optimizeFn({ task, userId });
    return response.data;
  } catch (error) {
    logger.error('AI 任務內容優化失敗:', error);
    throw new Error('AI task optimization failed');
  }
};

export const taskOperationMethods = {
  dropTasksByObjectiveId,
  splitTask,
  splitTaskWithAI,
  optimizeTaskContent,
};
